import i18n from '../../common/i18n';
import moment from 'moment';
import useLocalStorage from '../../common/hooks/useLocalStorage';
import { GoalOfMonthApi } from './types';
import { getGoalOfMonthData, getTvNovinyVoteUrl } from './api';
import { getWindowOptionsProperty, userContractId } from '../../common/util';
import { isBlank } from '../../common/utils/isBlank';
import { isBoolean } from 'lodash';
import { nuiCtx } from '../../common/initialization';
import { trackDwhVotingEvent } from '../dwh-tracking/api';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

const GOM_VOTE_AFTER_LOGIN = 'gom-vote-after-login';
const GOM_VOTE_AFTER_LOGIN_EVENT = 'gomAutomaticVoteEvent';

export function isGomCompetitionActive(config: GoalOfMonthApi.GoalOfMonthDriverCompetition) {
    if (!config) {
        return false;
    }

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const startDate = getSanitizedGomDate(config.start);
    const endDate = getSanitizedGomDate(config.end);

    const isEnabled = isBoolean() ? config.is_enabled : config.is_enabled === 'true';
    const isStarted = currentDate >= startDate;
    const isNotEnded = currentDate <= endDate;

    return isEnabled && isStarted && isNotEnded;
}

function getSanitizedGomDate(date?: string) {
    const slovakiaTimezone = 'Europe/Bratislava';
    const endDate = new Date(date);
    endDate.setHours(0, 0, 0, 0);
    endDate.toLocaleString('en-US', { timeZone: slovakiaTimezone });

    return endDate;
}

export async function getGomArticle(articleNiceUrl: string) {
    let article: { text: string };
    try {
        article = await nuiCtx.getArticle(articleNiceUrl);
    } catch (e) {
        article = { text: 'Článok neboj nájdený' };
    }
    return article;
}

function useGomVotedLocalStorageValue(competitionId: string, contractId: number) {
    const [gomStorageValue, setGomStorageValue] = useLocalStorage('gom', {});

    const setVoteForCompetition = useCallback(
        (itemId: string) => {
            setGomStorageValue(
                Object.assign({}, gomStorageValue, {
                    [`${competitionId}-${contractId}`]: itemId
                })
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [competitionId, contractId]
    );

    let voteForCompetition: string = null;

    if (!isBlank(competitionId) && !isBlank(contractId)) {
        voteForCompetition = gomStorageValue?.[`${competitionId}-${contractId}`];
    }

    return [voteForCompetition, setVoteForCompetition] as const;
}

function useGoalOfMonthApi() {
    return useQuery(['goal-of-month'], getGoalOfMonthData);
}

export function getGoalOfMonthConfig() {
    return {
        liveDataEndpointUrl: getWindowOptionsProperty<string>('gom.liveDataEndpointUrl'),
        liveDataDetailEndpointUrl: getWindowOptionsProperty<string>('gom.liveDataDetailEndpointUrl'),
        liveDataDriverEndpointUrl: getWindowOptionsProperty<string>('gom.liveDataDriverEndpointUrl'),
        voteWebUrl: getWindowOptionsProperty<string>('gom.voteWebUrl'),
        resultsWebUrl: getWindowOptionsProperty<string>('gom.resultsWebUrl'),
        statuteArticleNiceUrl: 'gol-mesiaca-statut-sutaze',
        gdprArticleNiceUrl: 'gol-mesiaca-gdpr',
        resultArticleNiceUrl: 'gol-mesiaca-odhlasovane-a-skoncene'
    };
}

const submitPlayerVote = async (player, title, competitionId) => {
    const trackVoteToDwh = async () => {
        const article = await getGomArticle(getGoalOfMonthConfig().gdprArticleNiceUrl);
        await trackDwhVotingEvent({
            itemId: player.id,
            itemName: player.title,
            competitionId: competitionId,
            competitionName: `${i18n('nui.gom.title')} - ${title}`,
            additionalData: JSON.stringify({ gdpr: article.text })
        });
    };

    const getUrlToNavigate = async () => {
        try {
            const { url } = await getTvNovinyVoteUrl({
                competitionId: competitionId,
                itemId: player.id
            });
            return url;
        } catch (e) {
            console.error(e);
            return Promise.resolve(getGoalOfMonthConfig().voteWebUrl);
        }
    };

    return Promise.all([trackVoteToDwh(), getUrlToNavigate()]).then(([, url]) => url);
};

export function useGoalOfMonth() {
    const contractId = userContractId();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data, status } = useGoalOfMonthApi();
    const [votedPlayerForCompetition, setVoteForCompetition] = useGomVotedLocalStorageValue(
        data?.competitionId,
        contractId
    );
    const getPlayerById = useCallback((itemId: string) => data?.players?.[itemId], [data?.players]);
    const votedPlayer = getPlayerById(votedPlayerForCompetition);

    const initiateAutoVote = async (itemId: string) => {
        const _votedPlayer = getPlayerById(itemId);
        localStorage.setItem(
            GOM_VOTE_AFTER_LOGIN,
            JSON.stringify({
                player: _votedPlayer,
                title: data?.title,
                competitionId: data?.competitionId,
                time: moment.now()
            })
        );
    };

    const handlePlayerVote = async (itemId: string) => {
        setIsSubmitting(true);
        setVoteForCompetition(itemId);
        const _votedPlayer = getPlayerById(itemId);
        return submitPlayerVote(_votedPlayer, data?.title, data?.competitionId);
    };

    useEffect(() => {
        const handleAutomaticVoteEvent = event => {
            if (event?.detail) {
                setVoteForCompetition(event?.detail?.itemId);
            }
        };
        document.addEventListener(GOM_VOTE_AFTER_LOGIN_EVENT, handleAutomaticVoteEvent);
        return () => {
            document.removeEventListener(GOM_VOTE_AFTER_LOGIN_EVENT, handleAutomaticVoteEvent);
        };
    }, [setVoteForCompetition]);

    return {
        votedPlayer,
        handlePlayerVote,
        data,
        status,
        isSubmitting,
        initiateAutoVote
    } as const;
}

export async function gomVoteAfterLogin() {
    const { player, title, competitionId, time } = JSON.parse(localStorage.getItem(GOM_VOTE_AFTER_LOGIN));
    const validValues = !isBlank(competitionId) && !isBlank(player?.id);
    const underMinute = moment.utc().diff(time, 'seconds') <= 60;
    const gomLocalStorageItem = JSON.parse(localStorage.getItem('gom'));
    const contractId = userContractId();
    const userAlreadyVoted =
        gomLocalStorageItem === null ? false : !!gomLocalStorageItem[`${competitionId}-${contractId}`];
    const voteCanProceed = !userAlreadyVoted && underMinute && validValues && !!contractId;
    if (voteCanProceed) {
        const event = new CustomEvent(GOM_VOTE_AFTER_LOGIN_EVENT, {
            detail: {
                itemId: player.id
            }
        });
        document.dispatchEvent(event);
        const url = await submitPlayerVote(player, title, competitionId);
        window.location.href = await url;
    }
}
